var searchFormDebounce =
  window.debounce ||
  function (fn, wait) {
    let timeout;

    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  };

class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        searchFormDebounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

if (!customElements.get('search-form')) {
  customElements.define('search-form', SearchForm);
}

class LegacyPredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.abortController = null;
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.statusElement = this.querySelector('.predictive-search-status');
    this.searchDelay = 200;
    this.search = searchFormDebounce(this.onSearch.bind(this), this.searchDelay);

    if (!this.input || !this.predictiveSearchResults) return;

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocClick.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange(event) {
    super.onChange(event);
    this.onSearch(event);
  }

  onSearch() {
    const query = this.getQuery();

    if (!query.length) {
      this.close();
      return;
    }

    this.getSearchResults(query);
  }

  onFocus() {
    const query = this.getQuery();
    const normalizedQuery = this.getCacheKey(query);
    if (!query.length) return;

    if (this.cachedResults[normalizedQuery]) {
      this.renderSearchResults(this.cachedResults[normalizedQuery]);
      return;
    }

    this.getSearchResults(query);
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      this.input.blur();
    }

    if (event.key === 'Enter' && this.input.form) {
      event.preventDefault();
      this.input.form.requestSubmit();
    }
  }

  onDocClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  getSearchResults(query) {
    const normalizedQuery = this.getCacheKey(query);

    if (this.cachedResults[normalizedQuery]) {
      this.renderSearchResults(this.cachedResults[normalizedQuery]);
      return;
    }

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    this.setAttribute('loading', '');

    const predictiveSearchUrl = window.Theme?.routes?.predictive_search_url || '/search/suggest';
    const url = new URL(predictiveSearchUrl, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', 'predictive-search');

    fetch(url.toString(), { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const html = new DOMParser().parseFromString(text, 'text/html');
        const resultsMarkup = html.querySelector('#predictive-search-results');
        const rendered = resultsMarkup ? resultsMarkup.outerHTML : text;

        this.cachedResults[normalizedQuery] = rendered;
        this.renderSearchResults(rendered);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.close();
      });
  }

  getCacheKey(query) {
    return query.replace(/\s+/g, '-').toLowerCase();
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.removeAttribute('loading');
    this.setAttribute('open', '');
    this.input.setAttribute('aria-expanded', 'true');

    if (this.statusElement) {
      this.statusElement.textContent = this.predictiveSearchResults.textContent.trim();
      this.statusElement.removeAttribute('aria-hidden');
    }
  }

  close() {
    this.removeAttribute('loading');
    this.removeAttribute('open');
    if (this.input) this.input.setAttribute('aria-expanded', 'false');
    if (this.predictiveSearchResults) this.predictiveSearchResults.innerHTML = '';
    if (this.statusElement) {
      this.statusElement.textContent = '';
      this.statusElement.setAttribute('aria-hidden', 'true');
    }
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', LegacyPredictiveSearch);
}
